import { defineStore } from 'pinia'
import { useAgentStore } from '~/stores/agent'
import { type ColorAPIResponse, type QueryProductsAPIResponseFilter, type CatalogProductsAPIQueryParams, type Filter, type FilterAPIResponse, type FilterValuesState, type ListedProduct, type PageResult, type ProductAPIResponse, type ProductListPayload, type QueryProductsAPIResponse, type SuggestionList, type UpdateQueryParamPayload, type ProductOfTheMonthAPIResponse, ShopPageQueryParam } from '~/types/shop'
import { addCommonQueryParam } from '~/utils/general'
import { filterConfig, filterFilterOptions, mapQueryParams, initialFilterValuesState, initialFilterOptionsState, mapValues, normalizeSlug, toListedProduct } from '~/utils/shop'
import { useCartStore } from '~/stores/cart'
import { useLocaleStore } from '~/stores/locale'
import { useFeaturesStore } from '~/stores/features'
import type { RouteQueryAndHash, RouteParamsGeneric } from "vue-router"
import { SHOP_ALL_PRODUCTS, SHOP_ROOT } from '~/utils/categoryRoutes'

type ProductListRoutePayload = Pick<{
  pathName: 'search' | 'category' | null
  path?: string
  query: RouteQueryAndHash['query']
  params: RouteParamsGeneric
}, 'pathName' | 'path' | 'query' | 'params'>

export const useShopStore = defineStore('shop', () => {
  const DEFAULT_QUERY_PARAMS = {
    pages: 1,
    sort: ''
  }

  const PAGE_SIZE_DESKTOP = 48 // 4 columns x 12 rows
  const PAGE_SIZE_TABLET = 24 // 3 columns x 8 rows
  const PAGE_SIZE_MOBILE = 18 // 2 columns x 9 rows
  const { $shopService, $api: api } = useNuxtApp()
  const agentStore = useAgentStore()
  const featuresStore = useFeaturesStore()

  const router = useRouter()
  const { breakpoints } = useBreakpoints()
  const pageSizeForBreakpoint = computed(() => {
    if (import.meta.server) {
      return PAGE_SIZE_DESKTOP
    }
    if (breakpoints.xs.value) {
      return PAGE_SIZE_MOBILE
    }
    if (breakpoints.sm.value) {
      return PAGE_SIZE_TABLET
    }
    return PAGE_SIZE_DESKTOP
  })

  // new path
  const pathName = ref<'search' | 'category' | null>()
  const routePath = ref<string>()
  const query = ref<RouteQueryAndHash['query']>()
  const params = ref<RouteParamsGeneric>()

  // previous request
  const term = ref<string>()
  const category = ref<string>()
  const orderBy = ref<string>()
  const viewAsDate = ref<string>()
  const filterValues = ref<FilterValuesState>(initialFilterValuesState())
  const loaded = ref<boolean>()
  const pagesLoaded = ref<number>(-1)
  const searchTypeLoaded = ref<string>()
  const viewedProducts = ref<ProductAPIResponse[]>([])

  // from backend
  const contentPages = ref<PageResult[]>()
  const products = ref<ListedProduct[]>()
  const moreToExploreProducts = ref<ProductAPIResponse[]>()
  let moreToExploreInFlight: Promise<void> | null = null
  const product = ref<ProductAPIResponse | null>()
  const allPage = ref<number>()
  const allResult = ref<number>()
  const allProduct = ref<number>()
  const filterOptions = ref<QueryProductsAPIResponseFilter>(initialFilterOptionsState())
  const suggestions = ref<SuggestionList>()
  const suggestedTerms = ref<string[]>()

  // static filters
  const staticFilters = ref<QueryProductsAPIResponseFilter | null>(null)
  const isStaticFiltersLoaded = ref<boolean>(false)

  const isPreorder = (qualifier: string[]) => {
    // once we are using JDE entirely for preorder remove 'preorder' as JDE send 'pre-order'
    return qualifier.includes('preorder') || qualifier.includes('pre-order')
  }

  const fetchSuggestions = async (searchTerm: string) => {
    return await api<SuggestionList>('suggestions', { query: { term: searchTerm } })
  }

  const fetchProductList = (withPages: boolean, params: CatalogProductsAPIQueryParams) => {
    return $shopService.fetchProductList(params, withPages, agentStore.viewAsDate)
  }

  const fetchMoreToExplore = async () => {
    if (moreToExploreProducts.value) {
      return
    }
    if (moreToExploreInFlight) {
      return moreToExploreInFlight
    }

    moreToExploreInFlight = (async () => {
      const query = addCommonQueryParam()
      const response = await api<QueryProductsAPIResponse>('products/more-to-explore', { query })
      moreToExploreProducts.value = response.products
    })()

    try {
      await moreToExploreInFlight
    } finally {
      moreToExploreInFlight = null
    }
  }

  const fetchProductOfTheMonth = async (months: number = 3) => {
    const query = addCommonQueryParam()
    const response = await api<ProductOfTheMonthAPIResponse>('products/potm', {
      query: {
        ...query,
        months
      }
    })
    return response
  }

  const fetchStaticFilters = async () => {
    try {
      const staticParams: CatalogProductsAPIQueryParams = {
        page: 1,
        pageSize: 0,
        stampSetLanguages: ['any']
      }
      const response = await $shopService.fetchProductList(staticParams, false, agentStore.viewAsDate)
      staticFilters.value = response
      isStaticFiltersLoaded.value = true
      return response
    } catch {
      // Fall back to dynamic behavior if static filter fetch fails, but never wipe
      // filters that an overlapping call already loaded
      if (!isStaticFiltersLoaded.value) {
        staticFilters.value = null
      }
      return null
    }
  }

  const mergeStaticWithDynamicFilters = (currentFilters: QueryProductsAPIResponseFilter): QueryProductsAPIResponseFilter => {
    if (!staticFilters.value || !isStaticFiltersLoaded.value) {
      // Fallback to current dynamic behavior if no static filters
      return currentFilters
    }

    type FilterOption = ColorAPIResponse | FilterAPIResponse
    const mergedFilterValues: Partial<Record<Filter, FilterOption[]>> = {}

    // Process each filter type from static filters
    Object.keys(staticFilters.value).forEach((filterKey) => {
      const filter = filterKey as Filter
      const staticFilterArray = staticFilters.value?.[filter]
      const currentFilterArray = currentFilters[filter]

      // If current API doesn't have this filter at all, skip it
      if (!currentFilterArray || !Array.isArray(currentFilterArray)) {
        return // Skip this filter entirely - continue to next filter
      }

      if (staticFilterArray && Array.isArray(staticFilterArray)) {
        // Create map of current filter values for quick lookup
        const currentFilterMap = new Map<string, FilterOption>()
        if (currentFilterArray && Array.isArray(currentFilterArray)) {
          currentFilterArray.forEach((item) => {
            if (item && typeof item === 'object' && 'value' in item) {
              currentFilterMap.set(item.value, item)
            }
          })
        }

        // Merge static filters with current counts
        const mergedFilterArray = staticFilterArray.map((staticItem) => {
          if (staticItem && typeof staticItem === 'object' && 'value' in staticItem) {
            const currentItem = currentFilterMap.get(staticItem.value)
            return {
              ...staticItem,
              count: currentItem ? currentItem.count : 0
            }
          }
          return staticItem
        })

        mergedFilterValues[filter] = mergedFilterArray
      }
    })

    return {
      totalPages: currentFilters.totalPages || 0,
      ...mergedFilterValues
    } as QueryProductsAPIResponseFilter
  }

  const querySuggestions = async (searchTerm: string) => {
    const response = await fetchSuggestions(searchTerm)
    suggestions.value = response
  }

  const queryAutoComplete = async (searchTerm: string) => {
    const response = await api<string[]>('autocomplete', { query: { term: searchTerm } })
    suggestedTerms.value = response
  }

  // Every filter or sort change starts a new queryProductList call and older calls are not
  // cancelled, so only the most recent call may write its response into the store.
  let latestProductListRequest = 0

  const queryProductList = async (payload: ProductListRoutePayload): Promise<ProductListPayload | null> => {
    const requestId = ++latestProductListRequest
    const isSuperseded = () => requestId !== latestProductListRequest
    pathName.value = payload.pathName
    routePath.value = payload.path
    query.value = payload.query
    params.value = payload.params


    // Fetch static filters on first category page visit if not already loaded.
    // Alongside the product list, not before it: the two queries are independent,
    // and awaiting one then the other put an extra backend round trip (~0.4-0.6 s)
    // in front of every first category render.
    const [, response] = await Promise.all([
      payload.pathName === 'category' && !isStaticFiltersLoaded.value ? fetchStaticFilters() : undefined,
      fetchProductList(payload.pathName === 'search', currentFetchQueryParams.value)
    ])
    if (isSuperseded()) {
      return null
    }

    // Merge static filters with current response if we're on a category page
    const finalFilterOptions = payload.pathName === 'category' && isStaticFiltersLoaded.value
      ? mergeStaticWithDynamicFilters(response)
      : response

    const gatedFilterOptions = filterFilterOptions(
      finalFilterOptions as QueryProductsAPIResponseFilter,
      featuresStore.enabledFeatures
    )

    const productList = {
      queryParams: {
        term: currentFetchQueryParamsBase.value.term,
        searchType: currentFetchQueryParamsBase.value.searchType,
        orderBy: currentFetchQueryParamsBase.value.orderBy,
        viewAsDate: currentFetchQueryParamsBase.value.viewAsDate,
        category: currentFetchQueryParamsBase.value.category,
        filterValues: filterQueryParams.value,
        pages: basicQueryParams.value.pages
      },
      products: (response.products ?? []).map(toListedProduct),
      allPage: response.totalPages,
      allResult: response.totalResults,
      allProduct: response.totalProducts,
      contentPages: response.contentResults,
      filterOptions: gatedFilterOptions
    } as ProductListPayload

    products.value = productList.products
    loaded.value = true
    term.value = productList.queryParams.term
    category.value = productList.queryParams.category
    orderBy.value = productList.queryParams.orderBy
    viewAsDate.value = productList.queryParams.viewAsDate
    filterValues.value = productList.queryParams.filterValues
    pagesLoaded.value = productList.queryParams.pages
    // Normalized exactly as useSearchResultTracking reads route.query.searchType, so the two are
    // comparable: the All tab sends no searchType at all.
    searchTypeLoaded.value = String(currentFetchQueryParamsBase.value.searchType ?? 'all')
    filterOptions.value = mapValues(productList.filterOptions)

    // Clear stale filter selections for filters whose options were stripped by the gate.
    for (const key of Object.keys(gatedFilterOptions) as Filter[]) {
      const options = gatedFilterOptions[key] as unknown[]
      if (options.length === 0 && (filterValues.value[key]?.length ?? 0) > 0) {
        filterValues.value[key] = []
      }
    }

    allPage.value = productList.allPage
    allResult.value = productList.allResult
    allProduct.value = productList.allProduct
    contentPages.value = productList.contentPages || []
    return productList
  }

  const fetchProductItem = async (title: string, itemId: string) => {
    const query = addCommonQueryParam()
    const cartStore = useCartStore()
    let loyaltyPercent = cartStore.summary?.promotionSummary?.find(p => p.loyaltyPromotion?.loyaltyPercentEarnings)?.loyaltyPromotion?.loyaltyPercentEarnings
    if (!loyaltyPercent && cartStore.cart?.id) {
      await cartStore.fetchCart()
      loyaltyPercent = cartStore.summary?.promotionSummary?.find(p => p.loyaltyPromotion?.loyaltyPercentEarnings)?.loyaltyPromotion?.loyaltyPercentEarnings
    }
    // Debug product request
    const localeStore = useLocaleStore()
    if (!localeStore.isLocaleSet) {
      const { $appInsights } = useNuxtApp()
      if ($appInsights) {
        $appInsights.trackTrace({
          message: 'Product request unknown locale',
          properties: {
            title,
            itemId,
            storeCulture: localeStore.culture,
            isLocaleSet: localeStore.isLocaleSet,
            baseUrl: localeStore.baseUrl,
            timestamp: new Date().toISOString()
          }
        })
      }
    }
    return await api<ProductAPIResponse>(`products/${encodeURIComponent(title)}`, {
      query: {
        ...query,
        itemId,
        loyaltyPercent
      }
    })
  }

  const queryProductItem = async (title: string, itemId: string) => {
    const productResponse = await fetchProductItem(title, itemId)
    updatedProductItem(productResponse)
    return product
  }

  const submitSubscription = async (email: string) => {
    const { paperPumpkinStatus }: { paperPumpkinStatus: number } = await api<{ paperPumpkinStatus: number }>(`subscriptions/${encodeURIComponent(email)}`)
    if (paperPumpkinStatus !== 1) {
      throw new Error('not subscribed')
    }
  }

  const updateQueryParam = (payload: UpdateQueryParamPayload) => {
    if (!router) {
      return
    }
    if (payload.filter === ShopPageQueryParam.Pages) {
      router.push({
        query: {
          ...router.currentRoute.value.query,
          pages: payload.value
        },
        params: {
          noScroll: 'true'
        }
      })
    } else if (payload.filter === ShopPageQueryParam.SortBy) {
      router.push({
        query: {
          ...router.currentRoute.value.query,
          sort: payload.value
        },
        params: {
          noScroll: 'true'
        }
      })
    } else if (payload.filter === ShopPageQueryParam.SortDesc) {
      /**
       * Null is treated specially by router:
       *
       * /?alpha
       *
       * Has following Query:
       * {
       *   alpha: null
       * }
       */
      router.push({
        query: {
          ...router.currentRoute.value.query,
          desc: payload.value ? null : undefined
        },
        params: {
          noScroll: 'true'
        }
      })
    } else if (payload.filter === ShopPageQueryParam.ResetAllFilters) {
      router.push({
        query: Object.fromEntries(
          Object.entries(router.currentRoute.value.query).filter(([key]) => !filterConfig[key as Filter])
        ),
        params: {
          noScroll: 'true'
        }
      })
    } else if (payload.filter === ShopPageQueryParam.Filters) {
      // reset to page 1 when filter changes (mobile)
      router.push({
        query: {
          ...router.currentRoute.value.query,
          ...payload.value,
          pages: '1'
        },
        params: {
          noScroll: 'true'
        }
      })
    } else { // payload.filter === ShopPageQueryParam.Filter
      // reset to page 1 when filter changes (desktop)
      router.push({
        query: {
          ...router.currentRoute.value.query,
          [payload.value[0]]: payload.value[1],
          pages: '1'
        },
        params: {
          noScroll: 'true'
        }
      })
    }
  }

  const updatedProductItem = (productPayload: ProductAPIResponse) => {
    product.value = productPayload
    if (viewedProducts.value.length > 10) {
      viewedProducts.value.shift()
    }
    if (!viewedProducts.value.some(p => p.id === productPayload.id)) {
      viewedProducts.value.push(productPayload)
    }
  }

  const recommendedProductsList = computed(() => {
    const cartStore = useCartStore()
    const cartProductIds = cartStore.items.map(item => item.productId)
    const recommendedProducts: ProductAPIResponse[] = []

    moreToExploreProducts.value?.forEach((moreToExploreProduct: ProductAPIResponse) => {
      if (!cartProductIds.includes(moreToExploreProduct.id)) {
        recommendedProducts.push(moreToExploreProduct)
      }
    })
    return recommendedProducts
  })

  const sortedViewedProducts = computed(() => {
    return [...viewedProducts.value].reverse().filter(p => p.itemPrice > 0)
  })

  const UNAVAILABLE_STATUSES = ['notorderable', 'discontinued']
  const isProductAvailable = (product: ProductAPIResponse) =>
        !UNAVAILABLE_STATUSES.includes(product.inventoryStatus ?? '');
  const isProductUnique = (product: ProductAPIResponse) =>
        !moreToExploreProducts.value?.some(p => p.id === product.id);
  const isNotInCart = (cartProductIds:  string[], productId: string) =>
        !cartProductIds.includes(productId);

  const productsToExploreList = computed(() => {
    const productsToExplore: ProductAPIResponse[] = []
    const cartStore = useCartStore()
    const cartProductIds = cartStore.items.map(item => item.productId)

    moreToExploreProducts.value?.forEach((moreToExploreProduct: ProductAPIResponse, index: number) => {
      if (!cartProductIds.includes(moreToExploreProduct.id)) {
        productsToExplore.push(moreToExploreProduct)
      }
      const viewedProduct = sortedViewedProducts.value[index]

      if (viewedProduct &&
          isProductAvailable(viewedProduct) &&
          isProductUnique(viewedProduct) &&
          isNotInCart(cartProductIds, viewedProduct.id)) {
        productsToExplore.push(viewedProduct)
      }
    })
    return productsToExplore
  })

  const clearProducts = () => {
    products.value = []
  }

  const clearContentPages = () => {
    contentPages.value = []
  }

  const clearSuggestions = () => {
    suggestions.value = { products: [], content: [] }
  }

  const formattedContentPages = computed(() => {
    if (!term.value) {
      return []
    }
    const re = new RegExp(`(${term.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig')
    return contentPages.value?.map(el => ({
      ...el,
      route: el.route.replace(/^~\//, '/'),
      title: el.title.replace(re, '<em>$1</em>'),
      description: el.description?.replace(re, '<em>$1</em>')
    }))
  })

  // A page size or page count from the URL, or the default when it is not a
  // positive whole number. The service sends 0 on purpose (the static-filter
  // query), so a typed ?pageSize=0 has to stop here: the backend answers it
  // with no products and a page count of 2147483647.
  const positiveQueryNumber = (value: unknown, fallback: number) => {
    const number = Number(Array.isArray(value) ? value[0] : value)
    return Number.isInteger(number) && number > 0 ? number : fallback
  }

  // For routes like /something?param - param is null, handling as true
  const basicQueryParams = computed(() => ({
    pageSize: positiveQueryNumber(query.value?.pageSize, pageSizeForBreakpoint.value),
    pages: positiveQueryNumber(query.value?.pages, DEFAULT_QUERY_PARAMS.pages),
    sort: Array.isArray(query.value?.sort)
      ? query.value.sort[0]
      : query.value?.sort || DEFAULT_QUERY_PARAMS.sort,
    desc: query.value?.desc === null
  }))

  const filterQueryParams = computed<Record<Filter, string[]>>(() => {
    const result: Record<Filter, string[]> = {} as Record<Filter, string[]>
    Object.keys(filterConfig).forEach((filterKey) => {
      if (query.value?.[filterKey]) {
        result[filterKey as Filter] = Array.isArray(query.value[filterKey])
          ? query.value[filterKey].filter((item): item is string => item !== null)
          : [query.value[filterKey] as string].filter((item): item is string => item !== null)
      }
    })
    return result
  })

  const currentFetchQueryParamsBase = computed(() => {
    const term = pathName.value === 'search'
      ? params.value?.term
      : undefined
    const category = pathName.value === 'category'
      ? (() => {
          const currentRoutePath = routePath.value || router.currentRoute.value.path
          const shopProductsIndex = currentRoutePath.indexOf(SHOP_ROOT)
          if (shopProductsIndex !== -1) {
            const categoryPath = currentRoutePath.substring(shopProductsIndex)
            return categoryPath === SHOP_ALL_PRODUCTS ? SHOP_ROOT : categoryPath
          }
          const slug = params.value?.pathMatch ?? params.value?.slug
          return normalizeSlug(Array.isArray(slug) ? slug : slug ? [String(slug)] : undefined)
        })()
      : undefined
    const searchType = query.value?.searchType
    const orderBy = `${basicQueryParams.value.sort}${basicQueryParams.value.desc ? ' desc' : ''}`
    const viewAsDate = agentStore.viewAsDate
    return {
      term,
      category,
      searchType,
      orderBy,
      viewAsDate
    }
  })

  const currentFetchQueryParams = computed<CatalogProductsAPIQueryParams>(() =>
    mapQueryParams({
      ...(
        {
          page: Number(basicQueryParams.value.pages),
          pageSize: Number(basicQueryParams.value.pageSize)
        }
      ),
      ...currentFetchQueryParamsBase.value,
      ...filterQueryParams.value
    })
  )

  return {
    products,
    product,
    suggestions,
    suggestedTerms,
    contentPages,
    allPage,
    pageSizeForBreakpoint,
    term,
    category,
    filterOptions,
    filterValues,
    allProduct,
    // Search telemetry reads these (useSearchResultTracking): loaded says a response has landed,
    // allResult is the real total on the Pages and Products tabs, and pagesLoaded and
    // searchTypeLoaded say which page and which tab the results are for. A setup store only
    // hydrates what it returns, so they also have to be here for a server-rendered search to
    // settle on the client.
    loaded,
    allResult,
    pagesLoaded,
    searchTypeLoaded,
    moreToExploreProducts,
    productsToExploreList,
    recommendedProductsList,
    clearProducts,
    clearContentPages,
    clearSuggestions,
    isPreorder,
    formattedContentPages,
    queryProductList,
    querySuggestions,
    queryAutoComplete,
    fetchProductItem,
    queryProductItem,
    fetchMoreToExplore,
    fetchProductOfTheMonth,
    submitSubscription,
    updatedProductItem,
    updateQueryParam,
    // static filters
    staticFilters,
    isStaticFiltersLoaded,
    fetchStaticFilters,
    mergeStaticWithDynamicFilters
  }
})
